// Adapter to MUX toast API. MUX provides a toast provider inside MuxProvider.
// This wrapper normalizes the API to provide .error/.success/.info/.warning
// so existing call sites (which used react-toastify) continue to work.
//
// MUX v4 useToast() returns a queue object: { push, pop, head, key, queue, concat, clear }
// push() accepts a string (rendered as informative) or { text, status, action, icon }.
// Status values: 'informative' | 'success' | 'error' (see Toast.STATUS).
import { useToast as useMuxToast } from '@manulife/mux';

type ToastContent = string | number | React.ReactNode;

// Use a string enum to keep the type compact for Sonar and maintain
// clear runtime string values used by the MUX API.
export enum ToastVariant {
  Info = 'info',
  Success = 'success',
  Error = 'error',
  Warning = 'warning'
}

/** Map our variant names to MUX Toast status constants. */
const VARIANT_TO_STATUS: Record<ToastVariant, string> = {
  [ToastVariant.Info]: 'informative',
  [ToastVariant.Success]: 'success',
  [ToastVariant.Error]: 'error',
  [ToastVariant.Warning]: 'informative' // MUX has no warning status; fall back to informative
};

export const useToast = () => {
  const mux: any = useMuxToast();

  const show = (content: ToastContent, type: ToastVariant = ToastVariant.Info, _duration?: number) => {
    if (!mux) {
      // eslint-disable-next-line no-console
      console[type === ToastVariant.Error ? 'error' : 'log'](content);
      return;
    }

    // MUX v4: useToast() returns a queue with push().
    // push(string) shows an informative toast; push({ text, status }) shows a styled toast.
    if (typeof mux.push === 'function') {
      const text = typeof content === 'string' ? content : String(content);
      const status = VARIANT_TO_STATUS[type];
      mux.push({ text, status });
      return;
    }

    // eslint-disable-next-line no-console
    console[type === ToastVariant.Error ? 'error' : 'log'](content);
  };

  const dismiss = () => {
    if (mux && typeof mux.clear === 'function') {
      mux.clear();
    }
  };

  return {
    show: (c: ToastContent, t?: ToastVariant, d?: number) => show(c, t, d),
    success: (c: ToastContent, d?: number) => show(c, ToastVariant.Success, d),
    error: (c: ToastContent, d?: number) => show(c, ToastVariant.Error, d),
    info: (c: ToastContent, d?: number) => show(c, ToastVariant.Info, d),
    warning: (c: ToastContent, d?: number) => show(c, ToastVariant.Warning, d),
    dismiss
  };
};

// A no-op component to provide a more standard export structure
export const Toast = () => null;
export default Toast;
